/**
 * Reducer for the builder's `DefinitionState`.
 *
 * The state shape is the exact JSON that will (later) be POSTed to
 * `createWorkflowDefinition` — inputs + nodes — plus a handful of builder-only
 * fields (`id`, `createdAt`, `updatedAt`, `layout`). No logic here depends on
 * a GraphQL client; everything is synchronous and testable.
 */

import { nanoid } from 'nanoid';

import {
  getPrimitive,
  INPUT_SUBTYPES,
  PRIMITIVE_BY_KEY
} from '~/pages/workflows/builder/fixtures/registry';
import { isInputLike } from './selectors';
import type {
  BindingSource,
  DefinitionNode,
  DefinitionState,
  ForEach,
  InputBinding,
  InputSubtype,
  TaskDefinitionKey,
  WorkflowDefinitionInputSlot
} from '~/pages/workflows/builder/types';

// ----- Helpers -----

function nodeId() {
  return `n_${nanoid(8)}`;
}

function slotKey() {
  return `slot_${nanoid(6)}`;
}

function defaultConfig(taskKey: TaskDefinitionKey): Record<string, unknown> {
  const primitive = PRIMITIVE_BY_KEY[taskKey];
  const config: Record<string, unknown> = {};
  if (!primitive) return config;
  for (const field of primitive.inputSchema) {
    // Only CONFIG fields with defaults go into the node config bag.
    if (field.fieldKind !== 'CONFIG') continue;
    if (field.default !== undefined) {
      config[field.key] = field.default;
    }
  }
  if (taskKey === 'EXTRACT') {
    config.entities = []; // array of EntitySpec
  }
  if (taskKey === 'OUTPUT_INTEGRATION') {
    config.actions = [];
  }
  if (taskKey === 'OUTPUT_DOCUMENT') {
    config.columns = [];
  }
  return config;
}

// ----- Factories -----

export function createEmptyState(label = 'Untitled workflow'): DefinitionState {
  const now = Date.now();
  return {
    id: `draft_${nanoid(10)}`,
    label,
    description: '',
    createdAt: now,
    updatedAt: now,
    inputs: [],
    nodes: []
  };
}

export function createNode(
  taskKey: TaskDefinitionKey,
  overrides: Partial<DefinitionNode> = {}
): DefinitionNode {
  return {
    id: overrides.id ?? nodeId(),
    taskKey,
    name: overrides.name,
    bindings: overrides.bindings ?? [],
    forEach: overrides.forEach,
    config: overrides.config ?? defaultConfig(taskKey),
    layout: overrides.layout,
    inputSubtype: overrides.inputSubtype,
    ownedSlotKey: overrides.ownedSlotKey,
    pinnedFields: overrides.pinnedFields
  };
}

// ----- Action types -----

export type Action =
  | { type: 'replace-state'; state: DefinitionState }
  | { type: 'rename'; label: string }
  | { type: 'add-node'; node: DefinitionNode }
  | { type: 'remove-node'; nodeId: string }
  | { type: 'rename-node'; nodeId: string; name: string }
  | { type: 'set-binding'; nodeId: string; inputKey: string; source: BindingSource | null }
  | { type: 'set-config'; nodeId: string; configKey: string; value: unknown }
  | { type: 'patch-config'; nodeId: string; patch: Record<string, unknown> }
  | { type: 'set-for-each'; nodeId: string; forEach: ForEach | null }
  | { type: 'toggle-pinned'; nodeId: string; configKey: string }
  | { type: 'add-slot'; slot: WorkflowDefinitionInputSlot }
  | { type: 'update-slot'; slotKey: string; patch: Partial<WorkflowDefinitionInputSlot> }
  | { type: 'remove-slot'; slotKey: string }
  | {
      type: 'set-input-subtype';
      nodeId: string;
      subtype: InputSubtype;
    }
  | {
      type: 'insert-between';
      fromNodeId: string;
      toNodeId: string;
      downstreamInputKey: string;
      newNode: DefinitionNode;
      newNodeInputKey: string;
      newNodeOutputKey: string;
      fromOutputKey: string;
    }
  | {
      type: 'add-parallel';
      siblingNodeId: string;
      newNode: DefinitionNode;
    };

// ----- Reducer -----

function touch(state: DefinitionState): DefinitionState {
  return { ...state, updatedAt: Date.now() };
}

function updateNode(
  state: DefinitionState,
  id: string,
  updater: (n: DefinitionNode) => DefinitionNode
): DefinitionState {
  return touch({
    ...state,
    nodes: state.nodes.map((n) => (n.id === id ? updater(n) : n))
  });
}

export function reducer(state: DefinitionState, action: Action): DefinitionState {
  switch (action.type) {
    case 'replace-state':
      return { ...action.state, updatedAt: Date.now() };
    case 'rename':
      return touch({ ...state, label: action.label });
    case 'add-node':
      return touch({ ...state, nodes: [...state.nodes, action.node] });
    case 'remove-node': {
      const target = state.nodes.find((n) => n.id === action.nodeId);
      if (!target) return state;
      // Clean up bindings/forEach references in other nodes
      const cleaned = state.nodes
        .filter((n) => n.id !== action.nodeId)
        .map((n) => {
          const bindings = n.bindings.filter(
            (b) => !(b.source.type === 'TASK_OUTPUT' && b.source.sourceNodeId === action.nodeId)
          );
          const forEach =
            n.forEach &&
            n.forEach.kind === 'TASK_OUTPUT' &&
            n.forEach.sourceNodeId === action.nodeId
              ? undefined
              : n.forEach;
          return { ...n, bindings, forEach };
        });
      // Remove owned slot too, if any
      const nextInputs = target.ownedSlotKey
        ? state.inputs.filter((s) => s.key !== target.ownedSlotKey)
        : state.inputs;
      return touch({ ...state, nodes: cleaned, inputs: nextInputs });
    }
    case 'rename-node':
      return updateNode(state, action.nodeId, (n) => ({ ...n, name: action.name }));
    case 'set-binding':
      return updateNode(state, action.nodeId, (n) => {
        const without = n.bindings.filter((b) => b.inputKey !== action.inputKey);
        if (action.source === null) {
          return { ...n, bindings: without };
        }
        const nextBinding: InputBinding = { inputKey: action.inputKey, source: action.source };
        return { ...n, bindings: [...without, nextBinding] };
      });
    case 'set-config':
      return updateNode(state, action.nodeId, (n) => ({
        ...n,
        config: { ...n.config, [action.configKey]: action.value }
      }));
    case 'patch-config':
      return updateNode(state, action.nodeId, (n) => ({
        ...n,
        config: { ...n.config, ...action.patch }
      }));
    case 'set-for-each':
      return updateNode(state, action.nodeId, (n) => ({
        ...n,
        forEach: action.forEach ?? undefined
      }));
    case 'toggle-pinned':
      return updateNode(state, action.nodeId, (n) => {
        const current = n.pinnedFields ?? [];
        const isPinned = current.includes(action.configKey);
        return {
          ...n,
          pinnedFields: isPinned
            ? current.filter((k) => k !== action.configKey)
            : [...current, action.configKey]
        };
      });
    case 'add-slot':
      return touch({ ...state, inputs: [...state.inputs, action.slot] });
    case 'update-slot':
      return touch({
        ...state,
        inputs: state.inputs.map((s) => (s.key === action.slotKey ? { ...s, ...action.patch } : s))
      });
    case 'remove-slot':
      return touch({
        ...state,
        inputs: state.inputs.filter((s) => s.key !== action.slotKey)
      });
    case 'set-input-subtype': {
      const node = state.nodes.find((n) => n.id === action.nodeId);
      if (!node) return state;
      const subtypeDef = INPUT_SUBTYPES.find((s) => s.key === action.subtype);
      if (!subtypeDef) return state;

      let nextInputs = state.inputs;
      let ownedSlotKey = node.ownedSlotKey;
      // Remove any existing owned slot
      if (ownedSlotKey) {
        nextInputs = nextInputs.filter((s) => s.key !== ownedSlotKey);
        ownedSlotKey = undefined;
      }
      // Materialize a slot for subtypes that need one
      if (subtypeDef.needsSlot) {
        const newSlot: WorkflowDefinitionInputSlot = {
          key: slotKey(),
          label: subtypeDef.defaultSlotLabel,
          kind: subtypeDef.slotKind ?? 'FILE',
          required: true,
          enumerable: false,
          acceptedMimeTypes: subtypeDef.acceptedMimeTypes
        };
        nextInputs = [...nextInputs, newSlot];
        ownedSlotKey = newSlot.key;
      }

      return touch({
        ...state,
        inputs: nextInputs,
        nodes: state.nodes.map((n) =>
          n.id === action.nodeId
            ? {
                ...n,
                inputSubtype: subtypeDef.key,
                taskKey: subtypeDef.taskKey,
                name: n.name ?? subtypeDef.defaultSlotLabel,
                ownedSlotKey,
                config: defaultConfig(subtypeDef.taskKey),
                bindings: [] // clear — subtype change invalidates bindings
              }
            : n
        )
      });
    }
    case 'insert-between': {
      const {
        fromNodeId,
        toNodeId,
        downstreamInputKey,
        newNode,
        newNodeInputKey,
        newNodeOutputKey,
        fromOutputKey
      } = action;
      // Wire new node's input to upstream
      const wiredNew: DefinitionNode = {
        ...newNode,
        bindings: [
          ...newNode.bindings.filter((b) => b.inputKey !== newNodeInputKey),
          {
            inputKey: newNodeInputKey,
            source: {
              type: 'TASK_OUTPUT',
              sourceNodeId: fromNodeId,
              sourceOutputKey: fromOutputKey
            }
          }
        ]
      };
      // Re-bind downstream's input to new node
      const rewired = state.nodes.map((n) => {
        if (n.id !== toNodeId) return n;
        return {
          ...n,
          bindings: n.bindings.map((b) =>
            b.inputKey === downstreamInputKey
              ? {
                  ...b,
                  source: {
                    type: 'TASK_OUTPUT',
                    sourceNodeId: newNode.id,
                    sourceOutputKey: newNodeOutputKey
                  } as BindingSource
                }
              : b
          )
        };
      });
      return touch({ ...state, nodes: [...rewired, wiredNew] });
    }
    case 'add-parallel': {
      const sibling = state.nodes.find((n) => n.id === action.siblingNodeId);
      if (!sibling) return state;
      // Clone upstream bindings (same inputs where keys match) and forEach.
      // Layout is left unset — dagre will place the new sibling at the same
      // rank automatically because they share upstream edges.
      const copied: DefinitionNode = {
        ...action.newNode,
        bindings: [...action.newNode.bindings],
        forEach: action.newNode.forEach ?? sibling.forEach
      };
      // Heuristic: copy sibling.bindings where input key matches by name+type
      const newPrim = getPrimitive(action.newNode.taskKey);
      for (const sib of sibling.bindings) {
        const target = newPrim.inputSchema.find(
          (f) => f.key === sib.inputKey && isInputLike(f.fieldKind)
        );
        if (target && !copied.bindings.some((b) => b.inputKey === sib.inputKey)) {
          copied.bindings.push({ ...sib });
        }
      }
      return touch({ ...state, nodes: [...state.nodes, copied] });
    }
    default:
      return state;
  }
}
